import { useEffect, useRef } from 'react';
import { useBookingStore } from '../store/useBookingStore';
import { useService } from './useBookingComputed';

/**
 * Hook to re-derive the booking duration when the selected service changes.
 *
 * Without this, a booking made against one service keeps that service's
 * duration after the Service field is changed: availability is still requested
 * for the old block size, so the slot grid keeps showing the old service's
 * intervals until the duration is changed by hand.
 */
export function useServiceDurationSync(): void {
	const { data: service } = useService();
	const applyServiceDuration = useBookingStore(
		(state) => state.applyServiceDuration
	);
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	// The store survives a GPPA-triggered React remount, so its service ID still
	// identifies the service whose duration is stored in the hidden input.
	const previousServiceIdRef = useRef<number | null>(
		currentServiceId > 0 ? currentServiceId : null
	);

	useEffect(() => {
		if (!service) {
			return;
		}

		const previousServiceId = previousServiceIdRef.current;
		previousServiceIdRef.current = service.id;

		// Only a switch between two services leaves a stale duration behind.
		// The first service to load must be left alone: its range is the value
		// restored from the hidden input on a multi-page form or an entry edit.
		if (previousServiceId === null || previousServiceId === service.id) {
			return;
		}

		applyServiceDuration(service);
	}, [service, applyServiceDuration]);
}
